import React, { useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import {
  BottomSheet,
  CategorySwitcher as CategorySwitcherLib,
  CategorySwitcherOption,
  Icon,
  Text,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';

const StyledCategorySwitcher = styled(CategorySwitcherLib)`
  width: 200px;

  & .spark__control {
    width: 200px;
  }
`;

const BottomSheetContent = styled.div`
  min-height: 140px;
`;

const MobileButton = styled.button`
  ${({ theme: { palette, borderRadius, spacing } }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-width: 200px;
    height: 40px;
    padding: ${spacing.s1} ${spacing.s3};
    color: ${palette.baseColors.lupin};
    border: 2px solid ${palette.baseColors.lupin};
    border-radius: ${borderRadius.r3};
    box-shadow: none;
    background: none;
  `}
`;

const Title = styled(Text)`
  ${({ theme: { spacing } }) => css`
    padding-right: ${spacing.s1_5};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 100%;
  `}
`;

const IconWrapper = styled.div`
  flex: 0 0 auto;
  line-height: 0;
`;

interface OptionRowProps {
  selected: boolean;
}

const OptionRow = styled.div<OptionRowProps>`
  ${({ theme: { palette, spacing }, selected }) => css`
    padding: ${spacing.s1_5};
    background-color: ${selected ? palette.baseColors.lupin : 'none'};
  `}
`;

type CatgeoryChangeArgs = {
  value: string;
  label: string;
};

interface OptionProps {
  closeBottomSheet: () => void;
  selectedSubcategoryId: string;
  onSubcategoryChange: ({ value, label }: CatgeoryChangeArgs) => void;
  subcategory: DeviceGalleryQuery['response']['deviceGallery']['subcategories'][0];
}

function Option({
  closeBottomSheet,
  subcategory,
  selectedSubcategoryId,
  onSubcategoryChange,
}: OptionProps) {
  const {
    palette: { baseColors },
  } = useTheme();

  function handleClick() {
    onSubcategoryChange({
      label: subcategory.name,
      value: subcategory.categoryId,
    });
    closeBottomSheet();
  }

  return (
    <OptionRow
      selected={selectedSubcategoryId === subcategory.categoryId}
      onClick={handleClick}
    >
      <Text
        variant="bodySMedium"
        color={
          selectedSubcategoryId === subcategory.categoryId
            ? baseColors.wairua
            : baseColors.newZealandBlack
        }
      >
        {subcategory.name}
      </Text>
    </OptionRow>
  );
}
interface CategorySwitcherProps {
  subcategories: DeviceGalleryQuery['response']['deviceGallery']['subcategories'];
  selectedSubcategoryId: string;
  selectedSubcategoryName: string;
  onSelectSubcategoryId: (subcategoryId: string) => void;
}

const CategorySwitcher = ({
  selectedSubcategoryId,
  selectedSubcategoryName,
  subcategories,
  onSelectSubcategoryId,
}: CategorySwitcherProps) => {
  const { palette } = useTheme();
  const isMobileView = useMediaQuery({ max: 'lil' });

  const { zIndex } = useTheme();

  const [isBottomSheetOpen, setBottomSheetOpen] = useState(false);

  const categoryOptions: CategorySwitcherOption<string>[] = subcategories.map(
    ({ categoryId, name }) => ({
      label: name,
      value: categoryId,
    }),
  );

  const selectedCategoryOption = categoryOptions.find(
    (categoryOption) => categoryOption.value === selectedSubcategoryId,
  );

  function handleCategoryChange({ value, label }: CatgeoryChangeArgs) {
    trackCustomLink(label);
    onSelectSubcategoryId(value);
  }

  function closeBottomSheet() {
    setBottomSheetOpen(false);
  }

  if (isMobileView) {
    return (
      <>
        <MobileButton
          onClick={() => setBottomSheetOpen(true)}
          data-testid="mobile-category-switch-button"
        >
          <Title variant="bodySMedium" color={palette.baseColors.lupin}>
            {selectedSubcategoryName}
          </Title>
          <IconWrapper>
            <Icon name="chevron_down_small" fill={palette.baseColors.lupin} />
          </IconWrapper>
        </MobileButton>
        <BottomSheet
          isOpen={isBottomSheetOpen}
          onClose={closeBottomSheet}
          zIndex={zIndex.modal}
        >
          <BottomSheetContent data-testid="mobile-category-switch-options">
            <LinkRegion regionId="filter.primaryCategory">
              {subcategories.map((subcategory) => (
                <Option
                  key={subcategory.categoryId}
                  closeBottomSheet={closeBottomSheet}
                  onSubcategoryChange={handleCategoryChange}
                  selectedSubcategoryId={selectedSubcategoryId}
                  subcategory={subcategory}
                />
              ))}
            </LinkRegion>
          </BottomSheetContent>
        </BottomSheet>
      </>
    );
  }

  return (
    <LinkRegion regionId="filter.primaryCategory">
      <StyledCategorySwitcher
        maxMenuHeight="210px"
        className="category-switcher"
        options={categoryOptions}
        placeholder="Category"
        selectedValue={selectedCategoryOption}
        onChange={handleCategoryChange}
        isSearchable={false}
      />
    </LinkRegion>
  );
};

export default CategorySwitcher;
